import { FunctionComponent, ReactNode, useCallback, useState } from "react";
import { useTranslation } from "react-i18next";

import { useGetMediaJobsQuery } from "@/pages/Private/redux/media/media.api";
import { PermissionRoles } from "@/enum";
import { classNames } from "@/utils/classNames";
import { useHasPermissions } from "@/utils/useHasPermissions";

import { IntroVideoTab } from "./IntroVideoTab";
import { JobsAndCostsTab } from "./JobsAndCostsTab";
import { VersionsTab } from "./VersionsTab";
import { CourseVersion } from "../../schema/courses";

type RightPanelTab = "graph" | "introVideo" | "jobsCosts" | "versions";

interface EditorRightPanelProps {
	courseId?: number;
	/** Passed through to the versions tab — see `VersionsTab.onView`. */
	onViewVersion?: (version: CourseVersion) => void;
	/** The scenario graph, rendered by the caller so this panel stays presentational. */
	graph: ReactNode;
}

/**
 * The editor's right-hand side. The graph stays mounted behind the other tabs rather than
 * being swapped out: React Flow holds pan, zoom and selection in component state, and
 * unmounting it would reset the author's view every time they glanced at the job list.
 */
export const EditorRightPanel: FunctionComponent<EditorRightPanelProps> = ({
	courseId,
	graph,
	onViewVersion,
}) => {
	const { t } = useTranslation();
	const te = useCallback((key: string) => t(`editor.rightPanel.${key}`), [t]);

	const [activeTab, setActiveTab] = useState<RightPanelTab>("graph");

	/**
	 * Previewing a version loads it into the flow — which is kept mounted but hidden behind
	 * whichever tab is open, and the author pressing this is by definition on Versions. So
	 * the load worked and landed on a screen nobody was looking at, which is indistinguishable
	 * from a button that does nothing. Showing the flow is the missing half of the action.
	 */
	const handleViewVersion = (version: CourseVersion) => {
		onViewVersion?.(version);
		setActiveTab("graph");
	};
	const { hasPermissions } = useHasPermissions();

	const { data: jobs } = useGetMediaJobsQuery(courseId ? { courseId, limit: 1 } : undefined, {
		skip: !courseId,
	});

	// Versions are an administrative view of the scenario rather than part of authoring it,
	// so the tab is not offered to anyone who could not act on it.
	const canManageVersions =
		hasPermissions([PermissionRoles.ADMIN]) || hasPermissions([PermissionRoles.GLOBAL_ADMIN]);

	const tabs: Array<{ key: RightPanelTab; count?: number }> = [
		{ key: "graph" },
		{ key: "introVideo" },
		{ key: "jobsCosts", count: jobs?.total },
		...(canManageVersions ? [{ key: "versions" as const }] : []),
	];

	return (
		<div className="flex h-full min-h-0 flex-col">
			<div className="flex shrink-0 gap-1 border-b border-border-soft">
				{tabs.map((tab) => {
					const isActive = activeTab === tab.key;

					return (
						<button
							key={tab.key}
							className={classNames(
								"flex items-center gap-2 border-b-2 px-4 py-3 text-sm font-medium transition-colors",
								isActive
									? "border-accent text-accent-strong"
									: "border-transparent text-subtle hover:text-text"
							)}
							type="button"
							onClick={() => setActiveTab(tab.key)}
						>
							{te(tab.key)}
							{tab.count ? (
								<span
									className={classNames(
										"rounded-[10px] border px-2 py-0.5 text-xs font-medium leading-[20px]",
										isActive
											? "border-accent bg-accent-tint text-text"
											: "border-transparent bg-surface text-subtle"
									)}
								>
									{tab.count}
								</span>
							) : null}
						</button>
					);
				})}
			</div>

			<div className="min-h-0 flex-1 pt-4">
				{/* Kept mounted, only hidden — see the component comment. */}
				<div className={classNames("h-full", activeTab === "graph" ? "" : "hidden")}>{graph}</div>

				{activeTab === "introVideo" && (
					<div className="h-full overflow-y-auto">
						<IntroVideoTab courseId={courseId} />
					</div>
				)}

				{activeTab === "jobsCosts" && courseId && (
					<div className="h-full overflow-y-auto">
						<JobsAndCostsTab courseId={courseId} />
					</div>
				)}

				{activeTab === "versions" && courseId && canManageVersions && (
					<div className="h-full overflow-y-auto">
						<VersionsTab courseId={courseId} onView={handleViewVersion} />
					</div>
				)}
			</div>
		</div>
	);
};
